import React, { useEffect } from 'react';
import { Mail, Phone, MapPin, MessageSquare, Clock, Shield, ExternalLink } from 'lucide-react';

const Contact = () => {
  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 gradient-hero" />
        <div className="absolute inset-0 pattern-bg" />
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-24">
          <div className="flex items-center gap-3 mb-6">
            <div className="icon-container w-12 h-12 rounded-xl flex items-center justify-center">
              <MessageSquare className="h-6 w-6 text-primary" />
            </div>
            <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Get in Touch</span>
          </div>
          
          <h1 className="font-display text-4xl lg:text-5xl xl:text-6xl text-foreground mb-6">
            Contact Us
          </h1>
          <p className="text-lg lg:text-xl text-muted-foreground max-w-2xl">
            Ready to discuss your situation? Our team is here to provide confidential, 
            no-obligation guidance.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="bg-background relative">
        <div className="absolute inset-0 pattern-bg opacity-20" />
        
        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 py-14 lg:py-20">
          <div className="text-center mb-12">
            <h2 className="font-display text-2xl lg:text-3xl text-foreground mb-4">
              Get in Touch
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              All inquiries are handled with complete confidentiality. Whether you're 
              just starting to think about selling or ready to move forward, we're 
              here to help.
            </p>
          </div>

          {/* Contact Cards */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <a 
              href="mailto:montie.barnes@fcbb.com"
              className="info-card p-8 rounded-2xl flex items-center gap-5 hover:border-primary/50 transition-smooth group"
            >
              <div className="icon-container w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0">
                <Mail className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-1">Email</h3>
                <p className="text-muted-foreground group-hover:text-primary transition-smooth">
                  montie.barnes@fcbb.com
                </p>
              </div>
            </a>

            <a 
              href="tel:9519019802"
              className="info-card p-8 rounded-2xl flex items-center gap-5 hover:border-primary/50 transition-smooth group"
            >
              <div className="icon-container w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0">
                <Phone className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-1">Phone</h3>
                <p className="text-muted-foreground group-hover:text-primary transition-smooth">
                  (951) 901-9802
                </p>
              </div>
            </a>
          </div>

          {/* Location Card */}
          <div className="info-card p-8 rounded-2xl mb-12">
            <div className="flex items-center gap-5 mb-6">
              <div className="icon-container w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0">
                <MapPin className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-1">Offices Nationwide</h3>
                <p className="text-muted-foreground">
                  With locations across the country, we're here to serve you locally.
                </p>
              </div>
            </div>
          </div>

          {/* What to Expect */}
          <div className="feature-card p-8 rounded-2xl">
            <h3 className="font-semibold text-foreground mb-6 flex items-center gap-2 text-lg">
              <Shield className="h-5 w-5 text-primary" />
              What to Expect
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Clock className="w-4 h-4 text-primary" />
                </div>
                <span className="text-muted-foreground">Initial response within one business day</span>
              </li>
              <li className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Shield className="w-4 h-4 text-primary" />
                </div>
                <span className="text-muted-foreground">Confidential, no-obligation consultation</span>
              </li>
              <li className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <MessageSquare className="w-4 h-4 text-primary" />
                </div>
                <span className="text-muted-foreground">Tailored guidance based on your situation</span>
              </li>
            </ul>
          </div>

          {/* Schedule CTA */}
          <div className="text-center mt-12">
            <a
              href="https://calendly.com/montie-barnesfcbb/30min"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary px-8 py-4 rounded-xl font-semibold inline-flex items-center gap-2 text-base"
            >
              Schedule a Consultation
              <ExternalLink className="h-5 w-5" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
